import { ChatMessage } from '../types'

export type ChatStreamHandlers = {
	onTitleSet?: (value: string) => void
	onDescription?: (value: string) => void
	onDelta?: (value: string) => void
	onFinal?: (value: string, meta: ChatMessage['meta']) => void
	onCallOperator?: () => void
	onShowPersonalAccount?: () => void
	onCloseChat?: () => void
	onAnswer?: (value: string) => void
	onError?: (error: Event | Error) => void
	onAddToBasket?: (value: string[]) => void
}

type ProcessChunk = {
	reader: ReadableStreamDefaultReader<Uint8Array>
	decoder: TextDecoder
	buf: string
}

export const createFetchChatStream = async (url: string, handlers: ChatStreamHandlers) => {
	const controller = new AbortController()

	const processChunk = async ({ reader, decoder, buf }: ProcessChunk): Promise<void> => {
		try {
			const { done, value } = await reader.read()
			if (done) return
			buf += decoder.decode(value, { stream: true })

			let idx: number
			while ((idx = buf.indexOf('\n\n')) !== -1) {
				const raw = buf.slice(0, idx)
				buf = buf.slice(idx + 2)

				const lines = raw.split(/\r?\n/)
				let data = ''
				for (const line of lines) {
					if (line.startsWith('data:')) {
						data += line.slice(5) + '\n'
					}
				}
				data = data.replace(/\n$/, '')

				if (!data) continue
				try {
					const payload = JSON.parse(data)
					const { operation, description, delta, answer, meta } = payload ?? {}

					if (typeof answer === 'string') {
						handlers.onAnswer?.(answer)
					}

					switch (operation) {
						case 'set_title_chat':
							if (typeof description === 'string') {
								handlers.onTitleSet?.(description)
							}
							break
						case 'step':
						case 'generating':
							if (typeof description === 'string') {
								handlers.onDescription?.(description)
							}
							break
						case 'delta':
							if (typeof delta === 'string') {
								handlers.onDelta?.(delta)
							}
							break
						case 'need_operator':
							handlers.onCallOperator?.()
							break
						case 'show_personal_account':
							handlers.onShowPersonalAccount?.()
							break
						case 'final':
							if (typeof answer === 'string') {
								handlers.onFinal?.(answer, meta)
							}

							if (typeof meta.add_to_basket.length) {
								handlers.onAddToBasket?.(meta.add_to_basket)
							}
							controller.abort()
							break

						case 'close_chat':
							handlers.onCloseChat?.()
							break
						default:
							break
					}
				} catch (err) {
					controller.abort()
					console.error('Failed to parse AI chat message', err)
				}
			}
			return processChunk({
				reader,
				decoder,
				buf
			})
		} catch (err) {
			// Игнорируем AbortError - это нормальное завершение стрима
			if (err instanceof Error && err.name === 'AbortError') {
				return
			}
			handlers.onError?.(err as unknown as Event)
			controller.abort()
		}
	}

	try {
		const res = await fetch(url, {
			signal: controller.signal,
			headers: { Accept: 'text/event-stream' }
		})

		if (!res.ok) {
			const data = await res.json().catch(() => null)
			if (data?.error === 'chat.disabled') {
				handlers.onCloseChat?.()
			} else {
				handlers.onError?.(new Error(data?.message || `AI chat request failed (${res.status})`))
			}
			return { close: () => controller.abort() }
		}

		if (!res.body) {
			throw new Error('Response has no body')
		}

		const reader = res.body.getReader()
		const decoder = new TextDecoder()
		const buf = ''

		processChunk({
			reader,
			decoder,
			buf
		})
	} catch (err) {
		handlers.onError?.(err as unknown as Event)
	}

	return {
		close: () => controller.abort()
	}
}
